import React, { useState } from 'react';
import axios from 'axios';

const TEN_MINUTES_MS = 10 * 60 * 1000;
const EXAMPLE_CSV = `datetimeUTC,Hs,Tp,waterDepth,RMS
2026-10-02T00:00:00Z,0.57,3.6,-39.8410056666667,38.7348353960593
2026-10-02T00:10:00Z,0.57,3.6,-39.8410056666667,41.5404161895739
2026-10-02T00:20:00Z,0.54,3.6,-39.8410056666667,44.2352755748343
2026-10-02T00:30:00Z,0.54,3.6,-39.8410056666667,31.0844305266692
2026-10-02T00:40:00Z,0.54,3.6,-39.8410056666667,31.5430317312873
2026-10-02T00:50:00Z,0.54,3.2,-39.8410056666667,38.370205403661
2026-10-02T01:00:00Z,0.54,3.2,-39.8410056666667,38.5566253751549
2026-10-02T01:10:00Z,0.54,3.2,-39.8410056666667,40.2398945377442
2026-10-02T01:20:00Z,0.51,3.3,-39.8410056666667,40.6083387045644
2026-10-02T01:30:00Z,0.51,3.3,-39.8410056666667,31.0131217059171
2026-10-02T01:40:00Z,0.51,3.3,-39.8410056666667,35.4349155393263
2026-10-02T01:50:00Z,0.56,3.2,-39.8410056666667,35.1744395827107
2026-10-02T02:00:00Z,0.56,3.2,-39.8410056666667,36.1820967072841
2026-10-02T02:10:00Z,0.56,3.2,-39.8410056666667,34.9083722730358
2026-10-02T02:20:00Z,0.57,3.1,-39.8410056666667,44.5170547103464
2026-10-02T02:30:00Z,0.57,3.1,-39.8410056666667,35.4745722007717`;

function parseCsvLine(line) {
  const values = [];
  let value = '';
  let quoted = false;

  for (let index = 0; index < line.length; index++) {
    const character = line[index];
    if (character === '"') {
      if (quoted && line[index + 1] === '"') {
        value += '"';
        index++;
      } else {
        quoted = !quoted;
      }
    } else if (character === ',' && !quoted) {
      values.push(value.trim());
      value = '';
    } else {
      value += character;
    }
  }
  if (quoted) throw new Error('CSV contains an unterminated quoted value');
  values.push(value.trim());
  return values;
}

function findColumn(headers, names) {
  const normalizedNames = names.map(name => name.toLowerCase());
  return headers.findIndex(header => normalizedNames.includes(header.trim().toLowerCase()));
}

function parseSGREForecastCsv(csvText) {
  const lines = String(csvText).replace(/^\uFEFF/, '').split(/\r?\n/).filter(line => line.trim());
  if (lines.length < 2) throw new Error('CSV must contain a header and at least one data row');

  const headers = parseCsvLine(lines[0]);
  const columns = {
    datetime: findColumn(headers, ['datetimeUTC', 't']),
    swh: findColumn(headers, ['swh', 'Hs']),
    pwp: findColumn(headers, ['pwp', 'Tp']),
    waterdepth: findColumn(headers, ['waterdepth', 'waterDepth']),
  };
  const missing = Object.entries(columns).filter(([, index]) => index < 0).map(([name]) => name);
  if (missing.length) throw new Error(`CSV is missing required column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`);

  const timestamps = [];
  const payload = { startdate: '', swh: [], pwp: [], waterdepth: [] };

  for (let lineIndex = 1; lineIndex < lines.length; lineIndex++) {
    const cells = parseCsvLine(lines[lineIndex]);
    const rowNumber = lineIndex + 1;
    const datetimeText = cells[columns.datetime];
    if (!/(?:Z|[+-]00:00)$/i.test(datetimeText ?? '')) {
      throw new Error(`Row ${rowNumber}: datetimeUTC must explicitly use UTC`);
    }
    const timestamp = new Date(datetimeText).getTime();
    if (!Number.isFinite(timestamp)) throw new Error(`Row ${rowNumber}: datetimeUTC is invalid`);

    for (const field of ['swh', 'pwp', 'waterdepth']) {
      const value = Number(cells[columns[field]]);
      if (!Number.isFinite(value) || cells[columns[field]] === '') {
        throw new Error(`Row ${rowNumber}: ${field} must be a number`);
      }
      payload[field].push(value);
    }
    timestamps.push(timestamp);
  }

  for (let index = 1; index < timestamps.length; index++) {
    if (timestamps[index] - timestamps[index - 1] !== TEN_MINUTES_MS) {
      throw new Error(`Rows ${index + 1} and ${index + 2} are not exactly 10 minutes apart`);
    }
  }

  payload.startdate = new Date(timestamps[0]).toISOString();
  return payload;
}

async function defaultSubmitPrediction({ backendUrl, user, payload }) {
  const response = await axios.post(`${backendUrl}/forecasts/predict`, payload, {
    headers: { Authorization: `Bearer ${user.token}` },
  });
  return response.data;
}

function messageFromError(error) {
  return error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Prediction failed';
}

function readFileText(file) {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Could not read CSV file'));
    reader.readAsText(file);
  });
}

function buildPredictionCsv(result) {
  const timestamps = Array.isArray(result?.timestamps) ? result.timestamps : [];
  const predictions = Array.isArray(result?.prediction) ? result.prediction : [];
  if (!timestamps.length || timestamps.length !== predictions.length) {
    throw new Error('Prediction result cannot be downloaded because its columns have different lengths');
  }

  const rows = ['datetimeUTC,prediction'];
  for (let index = 0; index < timestamps.length; index++) {
    const timestamp = new Date(timestamps[index]);
    const prediction = Number(predictions[index]);
    if (Number.isNaN(timestamp.getTime()) || !Number.isFinite(prediction)) {
      throw new Error('Prediction result contains invalid data');
    }
    rows.push(`${timestamp.toISOString()},${prediction}`);
  }
  return `${rows.join('\r\n')}\r\n`;
}

function predictionDownloadName(filename) {
  const baseName = String(filename || 'forecast').replace(/\.csv$/i, '');
  return `${baseName}_prediction.csv`;
}

function downloadPrediction(prediction) {
  const blob = new Blob([buildPredictionCsv(prediction.result)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = predictionDownloadName(prediction.filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function SGREForecastInferenceUpload({
  backendUrl,
  user,
  onPredictions,
  submitPrediction = defaultSubmitPrediction,
}) {
  const [files, setFiles] = useState([]);
  const [completedPredictions, setCompletedPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  const runPrediction = async () => {
    if (!files.length || loading) return;
    setLoading(true);
    setError('');
    const outcomes = await Promise.all(files.map(async (file, index) => {
      try {
        const payload = parseSGREForecastCsv(await readFileText(file));
        const result = await submitPrediction({ backendUrl, user, payload });
        return {
          prediction: {
            id: `${file.name}:${file.size}:${file.lastModified}:${index}`,
            filename: file.name,
            result,
          },
        };
      } catch (predictionError) {
        return { error: `${file.name}: ${messageFromError(predictionError)}` };
      }
    }));

    const predictions = outcomes.flatMap(outcome => outcome.prediction ? [outcome.prediction] : []);
    const errors = outcomes.flatMap(outcome => outcome.error ? [outcome.error] : []);
    try {
      setCompletedPredictions(predictions);
      if (predictions.length) onPredictions?.(predictions);
      setError(errors.join('\n'));
    } catch (predictionError) {
      setError(messageFromError(predictionError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section aria-label="Run uploaded forecast" style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <label>
          Forecast CSV files{' '}
          <input
            type="file"
            accept=".csv,text/csv"
            multiple
            onChange={event => {
              setFiles(Array.from(event.target.files ?? []));
              setCompletedPredictions([]);
              setError('');
            }}
          />
        </label>
        <button type="button" disabled={!files.length || loading} onClick={runPrediction}>
          {loading ? 'Running predictions…' : `Run prediction${files.length === 1 ? '' : 's'}`}
        </button>
        <button
          type="button"
          aria-label="Show CSV format example"
          title="Show CSV format example"
          onClick={() => setShowHelp(true)}
          style={{ borderRadius: '50%', width: 28, height: 28, padding: 0, fontWeight: 'bold' }}
        >
          ?
        </button>
      </div>
      {files.length > 0 && <small>{files.length} file{files.length === 1 ? '' : 's'} selected.</small>}
      {error && <div role="alert" style={{ color: 'tomato', whiteSpace: 'pre-line' }}>{error}</div>}
      {completedPredictions.length > 0 && (
        <div aria-label="Prediction downloads" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {completedPredictions.map(prediction => (
            <button
              key={prediction.id}
              type="button"
              onClick={() => {
                try {
                  downloadPrediction(prediction);
                } catch (downloadError) {
                  setError(`${prediction.filename}: ${messageFromError(downloadError)}`);
                }
              }}
            >
              Download {predictionDownloadName(prediction.filename)}
            </button>
          ))}
        </div>
      )}
      {showHelp && (
        <div
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) setShowHelp(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'grid',
            placeItems: 'center',
            padding: 24,
            background: 'rgba(0, 0, 0, 0.6)',
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="forecast-csv-example-title"
            style={{
              width: 'min(900px, 100%)',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: 20,
              borderRadius: 8,
              background: '#fff',
              color: '#111',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
              <h2 id="forecast-csv-example-title" style={{ margin: 0 }}>CSV format example</h2>
              <button type="button" onClick={() => setShowHelp(false)}>Close</button>
            </div>
            <p>Samples must be in UTC and exactly 10 minutes apart. The RMS column is optional and is ignored during inference.</p>
            <pre style={{ overflowX: 'auto', padding: 12, background: '#f4f4f4', borderRadius: 4 }}>
              <code>{EXAMPLE_CSV}</code>
            </pre>
          </section>
        </div>
      )}
    </section>
  );
}

export { buildPredictionCsv, defaultSubmitPrediction, EXAMPLE_CSV, parseSGREForecastCsv, predictionDownloadName };
